import React, { useState } from 'react';
import {
  Box,
  TextField,
  Checkbox,
  Button,
  FormControlLabel,
  Container,
  Typography,
  Card,
  CardContent,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { getResponsiveHeight, getResponsiveSize } from '../utils/responsive';
import { franchiseAPI } from '../utils/api';
import { useTranslation } from 'react-i18next';

const FranchiseForm = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { t } = useTranslation();

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    country: '',
    message: '',
    agreeToPrivacyPolicy: false,
  });

  const [submitStatus, setSubmitStatus] = useState(null); // 'success' or 'error'

  const handleChange = (event) => {
    const { name, value, checked, type } = event.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!formData.agreeToPrivacyPolicy) {
      alert(t('contactForm.formFields.privacyCheckbox'));
      return;
    }

    setSubmitStatus(null);

    const result = await franchiseAPI.submitFranchiseApplication(formData);

    if (result.success) {
      setSubmitStatus('success');
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        country: '',
        message: '',
        agreeToPrivacyPolicy: false,
      });
    } else {
      setSubmitStatus('error');
      console.error('API Error:', result.error);
    }
  };

  const inputFontSize = {
    xs: getResponsiveSize(18 * 0.7),
    md: getResponsiveSize(18 * 0.8),
    lg: getResponsiveSize(18),
  };

  const textFieldStyles = {
    '& .MuiOutlinedInput-root': {
      borderRadius: getResponsiveSize(12),
      fontFamily: '"Poppins", serif',
      fontWeight: 500,
      fontSize: inputFontSize,
      '& fieldset': {
        borderColor: 'rgba(0, 0, 0, 0.23)',
        fontFamily: '"Poppins", serif',
        fontWeight: 400,
      },
      '&:hover fieldset': {
        borderColor: '#65BA0D',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#65BA0D',
      },
      '& .MuiInputBase-input::placeholder': {
        fontFamily: '"Poppins", serif',
      },
    },
    '& .MuiInputLabel-root': {
      fontSize: inputFontSize,
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: '#65BA0D',
      fontFamily: '"Poppins", serif',
      fontWeight: 500,
    },
  };

  return (
    <Container sx={{ padding: isMobile ? '0 !important' : '' }}>
      <Card
        sx={{
          px: getResponsiveHeight(isMobile ? 2 : 4),
          mt: getResponsiveHeight(4),
          borderRadius: getResponsiveSize(8),
          boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
        }}
      >
        <CardContent>
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                mb: 2,
                flexDirection: isMobile ? 'column' : 'row',
              }}
            >
              <TextField
                fullWidth
                label={t('contactForm.formFields.firstName')}
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                placeholder={t('contactForm.formFields.firstName')}
                required
                sx={{ ...textFieldStyles, flex: 1 }}
              />
              <TextField
                fullWidth
                label={t('contactForm.formFields.lastName')}
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                placeholder={t('contactForm.formFields.lastName')}
                required
                sx={{ ...textFieldStyles, flex: 1 }}
              />
            </Box>
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                mb: 2,
                flexDirection: isMobile ? 'column' : 'row',
              }}
            >
              <TextField
                fullWidth
                label={t('contactForm.formFields.email')}
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder={t('contactForm.formFields.email')}
                required
                sx={{ ...textFieldStyles, flex: 1 }}
              />
              <TextField
                fullWidth
                label={t('contactForm.formFields.phone')}
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder={t('contactForm.formFields.phone')}
                sx={{ ...textFieldStyles, flex: 1 }}
              />
            </Box>

            <TextField
              fullWidth
              label={t('contactForm.formFields.country')}
              name="country"
              value={formData.country}
              onChange={handleChange}
              placeholder={t('contactForm.formFields.country')}
              sx={{ ...textFieldStyles, mb: 2 }}
            />

            <TextField
              fullWidth
              multiline
              rows={4}
              label={t('contactForm.formFields.comments')}
              name="message"
              value={formData.message}
              onChange={handleChange}
              placeholder={t('contactForm.formFields.comments')}
              sx={{ ...textFieldStyles, mb: 2 }}
            />

            <FormControlLabel
              control={
                <Checkbox
                  name="agreeToPrivacyPolicy"
                  checked={formData.agreeToPrivacyPolicy}
                  onChange={handleChange}
                  sx={{
                    color: '65BA0D',
                    '&.Mui-checked': {
                      color: '#65BA0D',
                    },
                    '&:hover': {
                      backgroundColor: 'rgba(184, 134, 11, 0.04)',
                    },
                  }}
                />
              }
              label={
                <Typography variant="regular_text_ur" sx={{ fontSize: inputFontSize }}>
                  {t('contactForm.formFields.privacyCheckbox')}
                </Typography>
              }
              sx={{ mb: 2 }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{
                mt: 2,
                mb: 2,
                bgcolor: ' #65BA0D',

                '&:hover': {
                  bgcolor: '#65BA0D',
                },
                borderRadius: '25px',
                py: 1.5,
              }}
            >
              {t('contactForm.formFields.submitButton')}
            </Button>

            {submitStatus === 'success' && (
              <Typography color="green" sx={{ mt: 1 }}>
                {t('contactForm.successMessage')}
              </Typography>
            )}
            {submitStatus === 'error' && (
              <Typography color="red" sx={{ mt: 1 }}>
                {t('contactForm.errorMessage')}
              </Typography>
            )}
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
};

export default FranchiseForm;